import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Tooltip,
  TextField,
} from "@mui/material";

import { Film, Media, Genre, FilmPremier } from "../types/interfaces";
import RateCanvas from "./RateCanvas";

import Api from "../api/Api";
import { BASE_API_URL, version } from "../api/const";
import { observer } from "mobx-react-lite";
import moviesStore from "../stores/moviesStore";

// Тип для позиции
interface HoverPosition {
  top: number;
  left: number;
}

const MainBodyPremier: React.FC = observer(() => {
  const [hoveredPoster, setHoveredPoster] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<HoverPosition>({
    top: 0,
    left: 0,
  });

  const [edited, setEdited] = useState<Record<number, Partial<FilmPremier>>>(
    {}
  );
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [loadButtonPushedIds, setLoadButtonPushedIds] = useState<Set<number>>(
    new Set()
  );
  const [addButtonPushedIds, setAddButtonPushedIds] = useState<Set<number>>(
    new Set()
  );
  const [addButtonLinksPushedIds, setAddButtonLinksPushedIds] = useState<
    Set<number>
  >(new Set());

  const [loadingLinks, setLoadingLinks] = useState<boolean>(false);

  const handleChange = (
    id: number,
    field: "kp_id" | "mbase_id" | "movie_id",
    value: string
  ) => {
    setEdited((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
    setSavedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id); // сбросить "сохранённый" статус
      return newSet;
    });
  };

  const handleUpdate = async (film: FilmPremier) => {
    // Составляем новые значения
    const newValues = {
      premier_id: film.premier_id, // первичный ключ
      kp_id: edited[film.id]?.kp_id ?? film.kp_id,
      mbase_id: edited[film.id]?.mbase_id ?? film.mbase_id,
      movie_id: edited[film.id]?.movie_id ?? film.movie_id,
    };
    // Проверяем, изменились ли значения
    const isChanged =
      newValues.kp_id !== film.kp_id ||
      newValues.mbase_id !== film.mbase_id ||
      newValues.movie_id !== film.movie_id;

    if (!isChanged) return; // Если изменений нет, ничего не делаем

    try {
      await Api.post(`${BASE_API_URL}/movies/premier/edit`, {
        premier_id: film.premier_id, // первичный ключ
        kp_id: edited[film.id]?.kp_id ?? film.kp_id,
        mbase_id: edited[film.id]?.mbase_id ?? film.mbase_id,
        movie_id: edited[film.id]?.movie_id ?? film.movie_id,
      });

      // подсвечиваем кнопку как успешно сохранённую
      setSavedIds((prev) => new Set(prev).add(film.id));

      await moviesStore.repeatLastPremierQuery();
    } catch (err) {
      console.error(err);
      alert("Не удалось сохранить изменения");
    }
  };

  const handleLoadButton = async (film: FilmPremier) => {
    try {
      await Api.post(`${BASE_API_URL}/movies/premier/load`, {
        premier_id: film.premier_id, // первичный ключ
      });
      // подсвечиваем кнопку как успешно сохранённую
      setLoadButtonPushedIds((prev) => new Set(prev).add(film.premier_id));
      await moviesStore.repeatLastPremierQuery();
    } catch (err) {
      console.error(err);
      alert("Не удалось вызвать load");
    }
  };
  const handleAddButton = async (film: FilmPremier) => {
    if (film.mbase_id === 0) return;
    try {
      await Api.post(`${BASE_API_URL}/movies/add`, {
        mbase_id: film.mbase_id, // первичный ключ
      });
      // подсвечиваем кнопку как успешно сохранённую
      setAddButtonPushedIds((prev) => new Set(prev).add(film.mbase_id));
      await moviesStore.repeatLastPremierQuery();
    } catch (err) {
      console.error(err);
      alert("Не удалось вызвать add");
    }
  };
  const handleAddButtonLinks = async (film: FilmPremier) => {
    try {
      setLoadingLinks(true);
      await Api.post(`${BASE_API_URL}/movies/premier/add`, {
        premier_id: film.premier_id,
      });
      // подсвечиваем кнопку как успешно сохранённую
      setAddButtonLinksPushedIds((prev) => new Set(prev).add(film.mbase_id));
      await moviesStore.repeatLastPremierQuery();
    } catch (err) {
      console.error(err);
      alert("Не удалось вызвать premier add");
    } finally {
      setLoadingLinks(false);
    }
  };

  const POSTER_WIDTH = 280;
  const POSTER_HEIGHT = 420;

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLTableCellElement>,
    element: Film
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    // базовое положение — под ячейкой
    let top = rect.top + window.scrollY - 85;
    const left = rect.left + window.scrollX + 100;

    const viewportBottom = window.scrollY + window.innerHeight;

    // проверка: влезает ли постер вниз
    if (top + POSTER_HEIGHT + 100 > window.innerHeight) {
      // если не влезает → рисуем ВВЕРХ
      top = rect.top + window.scrollY - POSTER_HEIGHT - 100;
    }

    setHoverPosition({ top, left });
    setHoveredPoster(element.logo);
  };

  const handleMouseLeave = () => {
    setHoveredPoster(null);
  };

  // if (loading)
  //   return (
  //     <Box display="flex" justifyContent="center" mt={4}>
  //       <CircularProgress />
  //     </Box>
  //   );

  // if (!movies.length)
  //   return (
  //     <Typography variant="body1" mt={4}>
  //       Нет фильмов
  //     </Typography>
  //   );

  function getSeasonsString(film: Film): string {
    if (!film.serial || !film.media) return "";

    // Фильтруем только те, у кого указан номер сезона
    const seasons = film.media
      .filter((s) => typeof s.season === "number")
      .sort((a, b) => a.season! - b.season!);

    if (seasons.length === 0) return "";

    let result: string[] = [];
    let current = seasons[0].season!;

    for (let i = 0; i < seasons.length; i++) {
      const s = seasons[i];

      // Добавляем "..." для пропущенных сезонов
      while (current < s.season!) {
        result.push("..");
        current++;
      }

      // Добавляем количество серий
      result.push(`(${s.items?.length ?? 0})`);
      current++;
    }

    return result.join(" ");
  }

  return (
    <>
      <Box sx={{ cursor: loadingLinks ? "progress" : "default" }}>
        {moviesStore.loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {!moviesStore.loading && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            mt={2}
            sx={{ width: "100vw", position: "relative" }}
          >
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                {/* Шапка */}
                <TableHead>
                  <TableRow
                    sx={{
                      "& td, & th": { fontWeight: "bold", textAlign: "center" },
                    }}
                  >
                    <TableCell>id</TableCell>
                    <TableCell>premier_id</TableCell>
                    <TableCell>kp_id</TableCell>
                    <TableCell>mbase_id</TableCell>
                    <TableCell>portal_id</TableCell>
                    <TableCell sx={{ minWidth: 380 }}>
                      сохранить/загрузить/добавить/добавить в карту
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>серии</TableCell>
                    <TableCell>title</TableCell>
                    <TableCell>year</TableCell>
                    <TableCell>age</TableCell>
                    <TableCell>is_active</TableCell>
                    <TableCell>last_updated_at</TableCell>
                  </TableRow>
                </TableHead>

                {/* Строки с фильмами */}
                <TableBody sx={{ "& td, & th": { textAlign: "center" } }}>
                  {moviesStore.moviesPremier.map((film) => (
                    <TableRow
                      key={film.id}
                      sx={{
                        "& td": {
                          padding: "2px 4px",
                          height: 40,
                        },
                      }}
                    >
                      {/* id */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.id}
                        </Typography>
                      </TableCell>

                      {/* premier_id */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.premier_id}
                        </Typography>
                      </TableCell>

                      {/* kp_id */}
                      <TableCell>
                        <TextField
                          size="small"
                          autoComplete="off"
                          placeholder={film.kp_id === 0 ? "0" : ""}
                          value={
                            edited[film.id]?.kp_id ??
                            (film.kp_id !== 0 ? film.kp_id : "")
                          }
                          onChange={(e) =>
                            handleChange(film.id, "kp_id", e.target.value)
                          }
                        />
                      </TableCell>

                      {/* mbase_id */}
                      <TableCell>
                        <TextField
                          size="small"
                          autoComplete="off"
                          placeholder={film.mbase_id === 0 ? "0" : ""}
                          value={
                            edited[film.id]?.mbase_id ??
                            (film.mbase_id !== 0 ? film.mbase_id : "")
                          }
                          onChange={(e) =>
                            handleChange(film.id, "mbase_id", e.target.value)
                          }
                        />
                      </TableCell>

                      {/* portal_id */}
                      <TableCell>
                        <TextField
                          size="small"
                          autoComplete="off"
                          placeholder={film.movie_id === 0 ? "0" : ""}
                          value={
                            edited[film.id]?.movie_id ??
                            (film.movie_id !== 0 ? film.movie_id : "")
                          }
                          onChange={(e) =>
                            handleChange(film.id, "movie_id", e.target.value)
                          }
                        />
                      </TableCell>

                      {/* сохранить/загрузить/добавить/добавить в карту */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between", // равномерно распределяем кнопки
                            alignItems: "center",
                            gap: 1, // равный отступ между кнопками
                          }}
                        >
                          <Tooltip
                            title="сохранить поля kp_id, mbase_id, movie_id"
                            arrow
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color={
                                  savedIds.has(film.id) ? "primary" : "inherit"
                                }
                                sx={{
                                  minWidth: 50,
                                  padding: "6px 6px",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.2,
                                }}
                                onClick={() => handleUpdate(film)}
                              >
                                сохранить
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title="получить информацию с сервиса Premier по сериям"
                            arrow
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color={
                                  loadButtonPushedIds.has(film.premier_id)
                                    ? "primary"
                                    : "inherit"
                                }
                                sx={{
                                  minWidth: 50,
                                  padding: "6px 6px",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.2,
                                }}
                                onClick={() => handleLoadButton(film)}
                              >
                                загрузить
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="влить карточку Mbase в  Portal" arrow>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={
                                  !(film.mbase_id !== 0 && film.movie_id === 0)
                                }
                                color={
                                  addButtonPushedIds.has(film.mbase_id)
                                    ? "primary"
                                    : "inherit"
                                }
                                sx={{
                                  minWidth: 50,
                                  padding: "6px 6px",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.2,
                                  bgcolor: !(
                                    film.mbase_id !== 0 && film.movie_id === 0
                                  )
                                    ? "grey.400"
                                    : undefined, // более серый цвет в неактивном состоянии
                                }}
                                onClick={() => handleAddButton(film)}
                              >
                                добавить
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="добавить линки в карточку" arrow>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={
                                  !film.movie_id ||
                                  (film.cnt_premier === film.cnt_portal &&
                                    film.cnt_premierBad === 0)
                                }
                                color={
                                  addButtonLinksPushedIds.has(film.mbase_id)
                                    ? "primary"
                                    : "inherit"
                                }
                                sx={{
                                  minWidth: 50,
                                  padding: "6px 6px",
                                  fontSize: "0.75rem",
                                  lineHeight: 1.2,
                                }}
                                onClick={() => handleAddButtonLinks(film)}
                              >
                                линки в карту
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* серии */}
                      <TableCell>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            color:
                              film.cnt_premier === 0
                                ? "red"
                              : film.cnt_premierBad > 0
                                ? "red"
                                : film.cnt_premier !== film.cnt_portal
                                ? "orange"
                                : "inherit",
                          }}
                        >
                          <Tooltip title="кол-во серий на премьере" arrow>
                            <span>{film.cnt_premier}</span>
                          </Tooltip>{" "}
                          /{" "}
                          <Tooltip title="кол-во серий с ошибкой" arrow>
                            <span>{film.cnt_premierBad}</span>
                          </Tooltip>{" "}
                          /{" "}
                          <Tooltip
                            title="кол-во серий на портале(premier)"
                            arrow
                          >
                            <span>{film.cnt_portal}</span>
                          </Tooltip>{" "}
                          /{" "}
                          <Tooltip
                            title="кол-во серий на портале(portal)"
                            arrow
                          >
                            <span>{film.cnt_portal_real}</span>
                          </Tooltip>{" "}
                        </Typography>
                      </TableCell>

                      {/* title */}
                      <TableCell
                        sx={{ "&.MuiTableCell-root": { textAlign: "left" } }}
                      >
                        <Tooltip title={film.review || ""} arrow>
                          {film.kp_id > 0 ? (
                            <a
                              href={`https://www.kinopoisk.ru/film/${film.kp_id}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "none",
                                color: "#1976d2",
                              }} // можно цвет темы MUI
                            >
                              <Typography variant="body1" fontWeight="bold">
                                {film.title}
                              </Typography>
                            </a>
                          ) : (
                            <Typography variant="body1" fontWeight="bold">
                              {film.title}
                            </Typography>
                          )}
                        </Tooltip>
                      </TableCell>

                      {/* year */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.year}
                        </Typography>
                      </TableCell>

                      {/* age */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.age}
                        </Typography>
                      </TableCell>

                      {/* is_active */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.is_active}
                        </Typography>
                      </TableCell>

                      {/* last_updated_at */}
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {film.last_updated_at}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </>
  );
});

export default MainBodyPremier;
